<div
  class="fixed inset-0 z-70 flex items-center justify-center bg-black/55 p-4"
  (click)="onBackdropClick()"
>
  <div
    #panel
    class="w-full overflow-hidden rounded-lg bg-white-light shadow-[0_30px_80px_rgba(0,0,0,0.22)]"
    [class]="panelClass()"
    (click)="$event.stopPropagation()"
    role="dialog"
    aria-modal="true"
    [attr.aria-labelledby]="titleId"
    [attr.aria-describedby]="message() ? messageId : null"
    [attr.aria-busy]="busy()"
    tabindex="-1"
  >
    <div [class]="referenceStyle() ? 'px-4 pt-3' : 'px-6 pt-4'">
      <div
        class="flex items-center gap-3 border-b border-[#F0F2F5]"
        [class.pb-3]="referenceStyle()"
        [class.pb-2]="!referenceStyle()"
      >
        <div
          class="flex shrink-0 items-center justify-center"
          [class]="
            showIconBackground()
              ? iconClass() + ' *:scale-150 rounded-full text-white size-11'
              : 'text-[#2187AB]'
          "
        >
          <ng-content select="[popupIcon]"></ng-content>
        </div>

        <h2
          [id]="titleId"
          class="flex-1 text-lg leading-7 font-semibold"
          [class]="titleClass()"
        >
          {{ title() }}
        </h2>

        @if (showCloseButton()) {
          <button
            type="button"
            (click)="onCloseClick()"
            [disabled]="busy()"
            class="inline-flex h-10 w-10 items-center justify-center rounded-full text-[#7A7A7A] transition hover:bg-slate-100 hover:text-[#4B5563] disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Close popup"
          >
            <svg
              class="h-5 w-5"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M5 5L15 15M15 5L5 15"
                stroke="currentColor"
                stroke-width="1.75"
                stroke-linecap="round"
              />
            </svg>
          </button>
        }
      </div>
    </div>

    <div class="py-5.5 px-7">
      @if (message()) {
        <p
          [id]="messageId"
          class="text-[15px] font-medium leading-[130%]"
          [class.text-[#1A1A1A]]="referenceStyle()"
          [class.text-gray]="!referenceStyle()"
        >
          {{ message() }}
        </p>
      }

      <ng-content></ng-content>
    </div>

    @if (hasButtons()) {
      <div
        class="flex flex-wrap items-center gap-3 pb-4"
        [class.px-4]="referenceStyle()"
        [class.px-6]="!referenceStyle()"
        [class.pb-7]="!referenceStyle()"
        [class]="buttonsContainerClass()"
      >
        @if (secondaryButton(); as secondary) {
          @if (isLinkAction(secondary)) {
            <a
              [routerLink]="secondary.link"
              [queryParams]="secondary.queryParams"
              (click)="onSecondaryClick($event)"
              [class.pointer-events-none]="busy() || secondary.disabled"
              [attr.aria-disabled]="busy() || secondary.disabled"
              class="inline-flex items-center justify-center rounded-lg text-white-darkest transition-colors hover:bg-slate-50"
              [class.py-2.5]="!referenceStyle()"
              [class.py-[9px]]="referenceStyle()"
              [class.text-sm]="!referenceStyle()"
              [class.text-base]="referenceStyle()"
              [class.min-w-32]="!referenceStyle()"
              [class.min-w-[109px]]="referenceStyle()"
              [class.px-6]="!referenceStyle()"
              [class.px-8]="referenceStyle()"
              [class.border]="!referenceStyle()"
              [class.border-[#D5DEE4]]="!referenceStyle()"
              [class.bg-[#F4F4F4]]="referenceStyle()"
              [class.bg-[#ffffff]]="!referenceStyle()"
              [class]="getActionWidthClass(secondary)"
            >
              {{ secondary.label }}
            </a>
          } @else {
            <button
              type="button"
              (click)="onSecondaryClick($event)"
              [disabled]="busy() || secondary.disabled"
              class="inline-flex items-center justify-center rounded-lg text-white-darkest transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              [class.py-2.5]="!referenceStyle()"
              [class.py-[9px]]="referenceStyle()"
              [class.text-sm]="!referenceStyle()"
              [class.text-base]="referenceStyle()"
              [class.min-w-32]="!referenceStyle()"
              [class.min-w-[109px]]="referenceStyle()"
              [class.px-6]="!referenceStyle()"
              [class.px-8]="referenceStyle()"
              [class.bg-[#F4F4F4]]="referenceStyle()"
              [class.bg-[#ffffff]]="!referenceStyle()"
              [class]="getActionWidthClass(secondary)"
            >
              {{ secondary.label }}
            </button>
          }
        }

        @if (primaryButton(); as primary) {
          @if (isLinkAction(primary)) {
            <a
              [routerLink]="primary.link"
              [queryParams]="primary.queryParams"
              (click)="onPrimaryClick($event)"
              [class.pointer-events-none]="busy() || primary.disabled"
              [attr.aria-disabled]="busy() || primary.disabled"
              data-popup-primary
              class="inline-flex items-center justify-center rounded-lg transition-colors"
              [class.py-2.5]="!referenceStyle()"
              [class.py-[9px]]="referenceStyle()"
              [class.text-sm]="!referenceStyle()"
              [class.text-base]="referenceStyle()"
              [class.min-w-38]="!referenceStyle()"
              [class.px-6]="!referenceStyle()"
              [class.px-8]="referenceStyle()"
              [class]="
                getPrimaryButtonClass(primary) +
                ' ' +
                getPrimaryActionWidthClass(primary)
              "
            >
              @if (busy()) {
                <span class="loading loading-spinner loading-xs me-2"></span>
              }
              {{ busy() ? primary.loadingLabel || primary.label : primary.label }}
            </a>
          } @else {
            <button
              type="button"
              (click)="onPrimaryClick($event)"
              [disabled]="busy() || primary.disabled"
              data-popup-primary
              class="inline-flex items-center justify-center rounded-lg transition-colors disabled:cursor-not-allowed disabled:opacity-70"
              [class.py-2.5]="!referenceStyle()"
              [class.py-[9px]]="referenceStyle()"
              [class.text-sm]="!referenceStyle()"
              [class.text-base]="referenceStyle()"
              [class.min-w-38]="!referenceStyle()"
              [class.px-6]="!referenceStyle()"
              [class.px-8]="referenceStyle()"
              [class]="
                getPrimaryButtonClass(primary) +
                ' ' +
                getPrimaryActionWidthClass(primary)
              "
            >
              @if (busy()) {
                <span class="loading loading-spinner loading-xs me-2"></span>
              }
              {{ busy() ? primary.loadingLabel || primary.label : primary.label }}
            </button>
          }
        }
      </div>
    }
  </div>
</div>
